export class ConnectivityUtils {
    /** Fast check — unreliable but instant (false = definitely offline) */
    static isOnline(): boolean {
        return typeof navigator !== 'undefined' ? navigator.onLine : true;
    }

    /** Real probe — pings a URL with a tight timeout. Caller provides the URL. */
    static async canReachServer(probeUrl: string, timeoutMs = 3000): Promise<boolean> {
        if (!ConnectivityUtils.isOnline()) return false;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
            const resp = await fetch(probeUrl, { method: 'GET', signal: controller.signal, cache: 'no-store' });
            clearTimeout(timeoutId);
            return resp.ok;
        } catch {
            return false;
        }
    }

    /** Classify error as connectivity-related */
    static isConnectivityError(err: any): boolean {
        return err?.name === 'UnknownHTTPError' || err?.name === 'NetworkError';
    }

    /** Broader check for upload retry — includes server 500s (e.g., MongoDB failures) */
    static isRetryableUploadError(err: any): boolean {
        return ConnectivityUtils.isConnectivityError(err) || err?.name === 'ServiceError';
    }

    /** Listen for browser online/offline events. SSR-safe. Returns cleanup function. */
    static onStatusChange(callback: (online: boolean) => void): () => void {
        if (typeof window === 'undefined') return () => {};
        const onOnline = () => callback(true);
        const onOffline = () => callback(false);
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }
}
